import React from 'react';
import { Alert, Box, CircularProgress, Stack } from '@mui/material';
import { ItemMasterStatusCard } from '../sections/ItemMasterStatusCard';
import { ItemMasterGrid } from '../grids/ItemMasterGrid';
import { useGetItemMasterEntriesQuery, useGetItemMasterFreshnessQuery } from '../../../claims';

interface ItemMasterTabProps {
  facilityId: string;
}

/**
 * Item Master tab (CAM-C10 US3). Owns both queries so the status card and grid share a single
 * loading and error state -- rendering their own stacked two spinners and two alerts for what is
 * one panel. The full facility result set is fetched once and `ItemMasterGrid` pages/sorts/filters
 * it client-side, mirroring `RoomsTab`.
 */
export const ItemMasterTab: React.FC<ItemMasterTabProps> = ({ facilityId }) => {
  const freshness = useGetItemMasterFreshnessQuery();
  const entries = useGetItemMasterEntriesQuery({ facilityId }, { skip: !facilityId });

  if (freshness.error || entries.error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Failed to load item master. Please try again.</Alert>
      </Box>
    );
  }

  if (freshness.isLoading || entries.isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" py={8}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  return (
    <Stack spacing={3}>
      <ItemMasterStatusCard
        facility={freshness.data?.facilities.find((f) => f.facilityId === facilityId)}
      />
      <ItemMasterGrid entries={entries.data?.entries ?? []} />
    </Stack>
  );
};
